import { useCallback, useState } from 'react'

import { Card, ErrorNote, Field, Modal, PageHeader, Spinner } from '@/components/ui'
import { useQuery } from '@/hooks/useQuery'
import { ApiError, api } from '@/lib/api'
import { useAuth } from '@/lib/auth'
import type { Ack, DangerSummary } from '@/types'

export function DangerPage() {
  const { user } = useAuth()
  const isOwner = user?.role === 'owner'
  const [confirming, setConfirming] = useState(false)
  const [phrase, setPhrase] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [notice, setNotice] = useState<string | null>(null)

  const summary = useQuery(
    useCallback(() => api.get<DangerSummary>('/danger/summary'), []),
    [],
    { enabled: isOwner },
  )

  function close() {
    setConfirming(false)
    setPhrase('')
    setError(null)
  }

  async function clearContacts() {
    setBusy(true)
    setError(null)
    try {
      const ack = await api.post<Ack>('/danger/clear-contacts', { confirm: phrase })
      setNotice(ack.detail ?? 'All contacts deleted.')
      close()
      summary.reload()
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'That did not work.')
    } finally {
      setBusy(false)
    }
  }

  const header = (
    <PageHeader
      title="Danger zone"
      subtitle="Irreversible operations. Nothing on this page can be undone."
    />
  )

  if (!isOwner) {
    return (
      <>
        {header}
        <ErrorNote message="Only an owner can use the danger zone." />
      </>
    )
  }
  if (summary.loading && !summary.data) return <Spinner label="Loading" />
  if (summary.error) return <ErrorNote message={summary.error} onRetry={summary.reload} />
  if (!summary.data) return null

  const counts = summary.data.contacts
  const required = summary.data.clear_contacts_phrase

  return (
    <>
      {header}

      {notice && (
        <p
          className="mb-4 rounded-md px-3 py-2 text-sm"
          role="status"
          style={{
            background: 'color-mix(in srgb, var(--good) 12%, transparent)',
            color: 'var(--good)',
          }}
        >
          {notice}
        </p>
      )}

      <Card
        title="Clear contacts"
        subtitle="Permanently delete every contact and everything derived from them"
        action={
          <button
            type="button"
            className="btn-danger"
            disabled={counts.contacts === 0}
            onClick={() => setConfirming(true)}
          >
            Clear contacts
          </button>
        }
      >
        <div className="space-y-3 px-4 py-4 text-sm">
          <dl className="grid grid-cols-2 gap-x-6 gap-y-3 sm:grid-cols-5">
            <Count label="Contacts" value={counts.contacts} />
            <Count label="Conversations" value={counts.conversations} />
            <Count label="Messages" value={counts.messages} />
            <Count label="Matches" value={counts.matches} />
            <Count label="Introductions" value={counts.introductions} />
          </dl>
          <p className="text-xs text-ink-muted">
            Profiles, opportunities, consent records and the network graph go too. Audit, webhook
            and error logs are kept, as are dashboard accounts.
          </p>
        </div>
      </Card>

      <Modal
        open={confirming}
        title="Clear all contacts"
        onClose={close}
        footer={
          <>
            <button type="button" className="btn-secondary" onClick={close} disabled={busy}>
              Cancel
            </button>
            <button
              type="button"
              className="btn-danger"
              disabled={busy || phrase.trim() !== required}
              onClick={() => void clearContacts()}
            >
              {busy ? 'Deleting…' : 'Delete everything'}
            </button>
          </>
        }
      >
        <div className="space-y-3 text-sm text-ink">
          <p>
            This permanently deletes{' '}
            <strong className="tabular">{counts.contacts}</strong> contacts along with their{' '}
            <span className="tabular">{counts.conversations}</span> conversations,{' '}
            <span className="tabular">{counts.messages}</span> messages and{' '}
            <span className="tabular">{counts.matches}</span> matches. It cannot be undone.
          </p>
          <Field label={`Type "${required}" to confirm`} error={error ?? undefined}>
            <input
              className="field"
              value={phrase}
              onChange={(event) => setPhrase(event.target.value)}
              autoComplete="off"
              spellCheck={false}
              autoFocus
            />
          </Field>
        </div>
      </Modal>
    </>
  )
}

function Count({ label, value }: { label: string; value: number }) {
  return (
    <div>
      <dt className="text-xs text-ink-faint">{label}</dt>
      <dd className="mt-0.5 tabular text-lg font-semibold text-ink">{value}</dd>
    </div>
  )
}
